/**
 * Platform-level business type (e.g. Restaurant, Construction, Cleaning).
 * Counter provides businessTypeId + guid; Firestore auto-generates docId.
 *
 * Read-only for tenants. Managed exclusively in the admin app by OrderUp team.
 * Restaurants reference these via businessTypeGuid.
 */

/**
 * Home-dashboard variants. Each value maps to a dashboard component in the
 * management app's dashboard registry:
 *   - `'operations'`   — restaurant/POS dashboard (the original, default).
 *   - `'financial'`    — OrderUp Books accounting workspace (QuickBooks-style).
 *   - `'appointments'` — appointments/booking vertical dashboard (calendar,
 *     provider availability, upcoming bookings). A HORIZONTAL capability
 *     available to ANY business type, not just the beauty/real-estate seeds.
 *
 * Stored on the business type so the choice is runtime-editable (admin app) and
 * extensible to future business types — NOT a hardcoded `name === "..."` check.
 */
export const DASHBOARD_LAYOUTS = [
  "operations",
  "financial",
  "appointments",
] as const;
export type DashboardLayout = (typeof DASHBOARD_LAYOUTS)[number];

/** Narrow an arbitrary value to a known DashboardLayout, defaulting to 'operations'. */
export function toDashboardLayout(value: unknown): DashboardLayout {
  return typeof value === "string" &&
    (DASHBOARD_LAYOUTS as readonly string[]).includes(value)
    ? (value as DashboardLayout)
    : "operations";
}

export interface IBusinessType {
  /** Platform counter ID (91000001+) */
  readonly businessTypeId: number;
  /** UUID for external references */
  readonly guid: string;
  /** Display name (e.g. "Restaurant", "Construction") */
  readonly name: string;
  /** Whether this business type is active */
  readonly isActive: boolean;
  /** Display sort order */
  readonly position: number;
  /** Management app nav sections enabled for this business type */
  readonly enabledNavSections: readonly string[];
  /**
   * Home-dashboard variant for this business type, resolved against the
   * management app's dashboard registry. Defaults to `'operations'`.
   */
  readonly dashboardLayout: DashboardLayout;
  /**
   * Top-level sidebar section titles to HIDE for this business type
   * (subtractive; default `[]` = hide nothing). Lets non-restaurant verticals
   * drop restaurant-centric sections without disturbing other business types.
   */
  readonly hiddenNavSections: readonly string[];
  /**
   * Ordered top-level sidebar section titles that float to the TOP of the nav
   * for this business type (in this order); sections not listed keep their
   * default relative order after. Default `[]` = unchanged order. Lets e.g.
   * finance verticals pin "OrderUp Books" right under "Dashboard".
   */
  readonly navOrder: readonly string[];
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

export interface IBusinessTypeData {
  businessTypeId: number;
  guid: string;
  name: string;
  isActive?: boolean;
  position?: number;
  enabledNavSections?: string[];
  dashboardLayout?: DashboardLayout;
  hiddenNavSections?: string[];
  navOrder?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export class BusinessType implements IBusinessType {
  readonly businessTypeId: number;
  readonly guid: string;
  readonly name: string;
  readonly isActive: boolean;
  readonly position: number;
  readonly enabledNavSections: readonly string[];
  readonly dashboardLayout: DashboardLayout;
  readonly hiddenNavSections: readonly string[];
  readonly navOrder: readonly string[];
  readonly createdAt: Date;
  readonly updatedAt: Date;

  constructor(data: IBusinessTypeData) {
    this.businessTypeId = data.businessTypeId;
    this.guid = data.guid;
    this.name = data.name;
    this.isActive = data.isActive ?? true;
    this.position = data.position ?? 0;
    this.enabledNavSections = data.enabledNavSections ?? [];
    this.dashboardLayout = data.dashboardLayout ?? "operations";
    this.hiddenNavSections = data.hiddenNavSections ?? [];
    this.navOrder = data.navOrder ?? [];
    this.createdAt = data.createdAt ?? new Date();
    this.updatedAt = data.updatedAt ?? new Date();
  }

  toJSON(): IBusinessType {
    return {
      businessTypeId: this.businessTypeId,
      guid: this.guid,
      name: this.name,
      isActive: this.isActive,
      position: this.position,
      enabledNavSections: this.enabledNavSections,
      dashboardLayout: this.dashboardLayout,
      hiddenNavSections: this.hiddenNavSections,
      navOrder: this.navOrder,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, unknown>): BusinessType {
    const businessTypeId =
      data.businessTypeId != null ? Number(data.businessTypeId) : 0;
    if (typeof data.guid !== "string")
      throw new Error("BusinessType.fromJSON: guid is missing.");
    if (typeof data.name !== "string")
      throw new Error("BusinessType.fromJSON: name is missing.");

    const convertTimestamp = (timestamp: unknown): Date | undefined => {
      if (!timestamp) return undefined;
      if (timestamp instanceof Date) return timestamp;
      if (
        timestamp &&
        typeof (timestamp as { toDate?: () => Date }).toDate === "function"
      ) {
        return (timestamp as { toDate: () => Date }).toDate();
      }
      if (
        timestamp &&
        typeof (timestamp as { seconds?: number }).seconds === "number"
      ) {
        const t = timestamp as { seconds: number; nanoseconds?: number };
        return new Date(
          t.seconds * 1000 + (t.nanoseconds ?? 0) / 1_000_000,
        );
      }
      const parsed = new Date(timestamp as string | number);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    };

    return new BusinessType({
      businessTypeId,
      guid: data.guid as string,
      name: data.name as string,
      isActive: data.isActive as boolean | undefined,
      position: data.position as number | undefined,
      enabledNavSections: Array.isArray(data.enabledNavSections)
        ? (data.enabledNavSections as string[])
        : undefined,
      dashboardLayout: toDashboardLayout(data.dashboardLayout),
      hiddenNavSections: Array.isArray(data.hiddenNavSections)
        ? (data.hiddenNavSections as string[])
        : undefined,
      navOrder: Array.isArray(data.navOrder)
        ? (data.navOrder as string[])
        : undefined,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
}
